import {
  POLL_YES_NO_MAYBE,
  type PollAudienceInput,
  type PollAudiencePreviewDto,
  type PollQuestionKind,
  type RoleDto,
} from '@ukinfo/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { fromDateTimeLocal, toDateTimeLocal } from '../lib/format';
import { useScope } from '../lib/scope';
import { PageHeader } from '../components/PageHeader';
import { PersonPicker } from '../components/PersonPicker';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Field,
  Input,
  LoadingBlock,
  SectionTitle,
  Textarea,
  cn,
  useToast,
} from '../components/ui';

/** Fragetyp im Formular. "Ja/Nein/Vielleicht" ist eine vorbelegte Einzelauswahl. */
type DraftKind = PollQuestionKind | 'YES_NO';

interface DraftQuestion {
  key: number;
  kind: DraftKind;
  text: string;
  required: boolean;
  options: string[];
}

const KIND_CHOICES: { kind: DraftKind; label: string }[] = [
  { kind: 'YES_NO', label: 'Ja / Nein / Vielleicht' },
  { kind: 'SINGLE', label: 'Eine Antwort' },
  { kind: 'MULTIPLE', label: 'Mehrere Antworten' },
  { kind: 'TEXT', label: 'Freitext' },
];

let nextKey = 1;
const newQuestion = (kind: DraftKind = 'YES_NO'): DraftQuestion => ({
  key: nextKey++,
  kind,
  text: '',
  required: true,
  options: kind === 'YES_NO' ? [...POLL_YES_NO_MAYBE] : kind === 'TEXT' ? [] : ['', ''],
});

/** Vorschau der Empfaenger erst nach einer kurzen Tipp-Pause laden. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function PollCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const scope = useScope();

  const units = useQuery({ queryKey: ['poll-units'], queryFn: () => api.get<string[]>('/api/polls/units') });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleDto[]>('/api/roles'),
    select: (list) => list.filter((role) => !role.archived),
  });

  const allowedUnits = useMemo(() => {
    const allowed = new Set(units.data ?? []);
    return scope.units
      .filter((unit) => allowed.has(unit.id))
      .sort((a, b) => a.path.join('/').localeCompare(b.path.join('/'), 'de'));
  }, [units.data, scope.units]);

  const [unitId, setUnitId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<'UNIT' | 'PERSONS'>('UNIT');
  const [includeChildren, setIncludeChildren] = useState(true);
  const [includeRoster, setIncludeRoster] = useState(true);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [personIds, setPersonIds] = useState<string[]>([]);
  const [questions, setQuestions] = useState<DraftQuestion[]>(() => [newQuestion()]);
  const [closesAt, setClosesAt] = useState(() => toDateTimeLocal(new Date(Date.now() + 7 * 86400_000).toISOString()));
  const [anonymous, setAnonymous] = useState(false);
  const [showResults, setShowResults] = useState(true);
  const [showRecipients, setShowRecipients] = useState(false);

  // Vorbelegt mit dem Bereich aus dem Kopf, sofern dort Umfragen erlaubt sind.
  useEffect(() => {
    if (unitId || allowedUnits.length === 0) return;
    const preferred = allowedUnits.find((unit) => unit.id === scope.unitId) ?? allowedUnits[0];
    if (preferred) setUnitId(preferred.id);
  }, [allowedUnits, scope.unitId, unitId]);

  const audience: PollAudienceInput = { mode, includeChildren, includeRoster, roleIds, personIds };
  const previewKey = useDebounced(JSON.stringify({ organisationUnitId: unitId, audience }));
  const preview = useQuery({
    queryKey: ['poll-audience', previewKey],
    queryFn: () => api.post<PollAudiencePreviewDto>('/api/polls/audience-preview', JSON.parse(previewKey)),
    enabled: Boolean(unitId),
    placeholderData: (previous) => previous,
  });

  const updateQuestion = (key: number, patch: Partial<DraftQuestion>): void =>
    setQuestions((list) => list.map((q) => (q.key === key ? { ...q, ...patch } : q)));

  const problems: string[] = [];
  if (!title.trim()) problems.push('Titel fehlt');
  if (!unitId) problems.push('Bereich fehlt');
  if ((preview.data?.count ?? 0) === 0) problems.push('Niemand ausgewählt');
  questions.forEach((q, index) => {
    if (!q.text.trim()) problems.push(`Frage ${index + 1} ohne Text`);
    const filled = q.options.map((o) => o.trim()).filter(Boolean);
    if (q.kind !== 'TEXT' && filled.length < 2) problems.push(`Frage ${index + 1}: mindestens zwei Antworten`);
    if (new Set(filled.map((o) => o.toLowerCase())).size !== filled.length) problems.push(`Frage ${index + 1}: Antwort doppelt`);
  });
  const deadline = fromDateTimeLocal(closesAt);
  if (deadline && deadline <= new Date().toISOString()) problems.push('Frist liegt in der Vergangenheit');

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; recipientCount: number }>('/api/polls', {
        organisationUnitId: unitId,
        title: title.trim(),
        description: description.trim() || null,
        closesAt: deadline,
        anonymous,
        showResults,
        audience,
        questions: questions.map((q) => ({
          kind: q.kind === 'YES_NO' ? 'SINGLE' : q.kind,
          text: q.text.trim(),
          required: q.required,
          options: q.kind === 'TEXT' ? [] : q.options.map((o) => o.trim()).filter(Boolean),
        })),
      }),
    onSuccess: (result) => {
      toast.show(`Umfrage an ${result.recipientCount} Personen gestartet`);
      navigate(`/umfragen/${result.id}`, { replace: true });
    },
    onError: (err: unknown) => toast.show(err instanceof ApiError ? err.message : 'Umfrage konnte nicht gestartet werden', 'error'),
  });

  if (units.isLoading) return <LoadingBlock />;

  const withoutLogin = preview.data?.persons.filter((p) => !p.hasLogin).length ?? 0;

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (problems.length === 0) create.mutate();
      }}
    >
      <Link to="/umfragen" className="text-sm font-medium text-brand-700">← Umfragen</Link>
      <PageHeader title="Neue Umfrage" description="Fragen stellen, Antworten sammeln, Ergebnis sehen" scoped={false} />

      <Card>
        <CardBody className="space-y-4">
          <Field label="Titel" required>
            {(id) => <Input id={id} value={title} maxLength={150} onChange={(e) => setTitle(e.target.value)} placeholder="z. B. Trainingszeiten im Winter" />}
          </Field>
          <Field label="Beschreibung" hint="Optional – worum geht es, was passiert mit dem Ergebnis?">
            {(id) => <Textarea id={id} rows={3} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} />}
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-4">
          <SectionTitle title="Wer wird gefragt?" />
          <Field label="Bereich" required hint="Die Umfrage gehört zu diesem Bereich. Nur Bereiche, in denen du Umfragen starten darfst.">
            {(id) => (
              <select id={id} className="input" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                {allowedUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {' '.repeat(unit.depth * 3)}{unit.depth > 0 ? '└ ' : ''}{unit.name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <div role="radiogroup" aria-label="Empfänger" className="grid gap-2 sm:grid-cols-2">
            {([
              ['UNIT', 'Mitglieder des Bereichs', 'Funktionen und Kader – optional eingeschränkt'],
              ['PERSONS', 'Ausgewählte Personen', 'Nur die Personen, die du auswählst'],
            ] as const).map(([value, label, hint]) => (
              <label
                key={value}
                className={cn(
                  'flex cursor-pointer gap-2 rounded-lg border px-3 py-2.5 text-sm',
                  mode === value ? 'border-brand-500 bg-brand-50' : 'border-slate-200 hover:border-slate-300',
                )}
              >
                <input type="radio" name="mode" className="mt-0.5 h-4 w-4 accent-brand-600" checked={mode === value} onChange={() => setMode(value)} />
                <span>
                  <span className="block font-medium text-slate-800">{label}</span>
                  <span className="block text-xs text-slate-500">{hint}</span>
                </span>
              </label>
            ))}
          </div>

          {mode === 'UNIT' && (
            <div className="space-y-3">
              <Checkbox label="Untergeordnete Bereiche einbeziehen" checked={includeChildren} onChange={setIncludeChildren} />
              <Checkbox label="Spieler aus dem Kader der Mannschaften (laufende Saison)" checked={includeRoster} onChange={setIncludeRoster} />
              <div>
                <p className="label">Nur bestimmte Funktionen</p>
                <div className="flex flex-wrap gap-1.5">
                  {(roles.data ?? []).map((role) => {
                    const on = roleIds.includes(role.id);
                    return (
                      <button
                        key={role.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => setRoleIds((ids) => (on ? ids.filter((id) => id !== role.id) : [...ids, role.id]))}
                        className={cn(
                          'rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
                          on ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-700 ring-slate-300 hover:ring-slate-400',
                        )}
                      >
                        {role.name}
                      </button>
                    );
                  })}
                </div>
                <p className="hint">{roleIds.length === 0 ? 'Keine gewählt: alle Funktionen im Bereich.' : 'Nur Personen mit den gewählten Funktionen.'}</p>
              </div>
            </div>
          )}

          <Field label={mode === 'UNIT' ? 'Zusätzliche Personen' : 'Personen'} required={mode === 'PERSONS'}>
            {(id) => <PersonPicker id={id} value={personIds} onChange={setPersonIds} placeholder="Name eingeben …" />}
          </Field>

          <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm" aria-live="polite">
            {preview.isFetching && !preview.data ? (
              <span className="text-slate-500">Empfänger werden ermittelt …</span>
            ) : preview.error ? (
              <span className="text-rose-700">{preview.error instanceof ApiError ? preview.error.message : 'Empfänger konnten nicht ermittelt werden'}</span>
            ) : (
              <>
                <span className="font-medium text-slate-800">
                  {preview.data?.count ?? 0} {preview.data?.count === 1 ? 'Person wird' : 'Personen werden'} gefragt
                </span>
                {(preview.data?.count ?? 0) > 0 && (
                  <button type="button" className="ml-2 text-brand-700 hover:underline" onClick={() => setShowRecipients((v) => !v)}>
                    {showRecipients ? 'ausblenden' : 'anzeigen'}
                  </button>
                )}
                {withoutLogin > 0 && (
                  <span className="mt-1 block text-xs text-slate-500">
                    {withoutLogin} ohne Zugang – sie antworten über ihren persönlichen Sport-Link.
                  </span>
                )}
                {showRecipients && (
                  <ul className="mt-2 columns-1 gap-4 text-slate-700 sm:columns-2">
                    {preview.data?.persons.map((p) => (
                      <li key={p.personId}>
                        {p.name}
                        {!p.hasLogin && <span className="text-xs text-slate-400"> (Link)</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-4">
          <SectionTitle title="Fragen" />
          {questions.map((question, index) => (
            <div key={question.key} className="space-y-3 rounded-xl border border-slate-200 p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-700">Frage {index + 1}</span>
                {questions.length > 1 && (
                  <Button type="button" size="sm" variant="ghost" onClick={() => setQuestions((list) => list.filter((q) => q.key !== question.key))}>
                    Entfernen
                  </Button>
                )}
              </div>
              <Field label="Frage" required>
                {(id) => (
                  <Input id={id} value={question.text} maxLength={300} onChange={(e) => updateQuestion(question.key, { text: e.target.value })} placeholder="z. B. Kommst du zur Weihnachtsfeier?" />
                )}
              </Field>
              <Field label="Antwortart">
                {(id) => (
                  <select
                    id={id}
                    className="input"
                    value={question.kind}
                    onChange={(e) => {
                      const kind = e.target.value as DraftKind;
                      const fresh = newQuestion(kind);
                      // Selbst eingegebene Antworten bleiben beim Wechsel zwischen Einzel- und Mehrfachauswahl.
                      const keep = (kind === 'SINGLE' || kind === 'MULTIPLE') && (question.kind === 'SINGLE' || question.kind === 'MULTIPLE');
                      updateQuestion(question.key, { kind, options: keep ? question.options : fresh.options });
                    }}
                  >
                    {KIND_CHOICES.map((choice) => <option key={choice.kind} value={choice.kind}>{choice.label}</option>)}
                  </select>
                )}
              </Field>
              {question.kind !== 'TEXT' && (
                <div className="space-y-2">
                  <p className="label">Antwortmöglichkeiten</p>
                  {question.options.map((option, position) => (
                    <div key={position} className="flex gap-2">
                      <Input
                        aria-label={`Antwort ${position + 1} zu Frage ${index + 1}`}
                        value={option}
                        maxLength={200}
                        onChange={(e) => updateQuestion(question.key, { options: question.options.map((o, i) => (i === position ? e.target.value : o)) })}
                      />
                      {question.options.length > 2 && (
                        <Button
                          type="button"
                          variant="ghost"
                          aria-label={`Antwort ${position + 1} entfernen`}
                          onClick={() => updateQuestion(question.key, { options: question.options.filter((_, i) => i !== position) })}
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                  {question.options.length < 20 && (
                    <Button type="button" size="sm" variant="secondary" onClick={() => updateQuestion(question.key, { options: [...question.options, ''] })}>
                      + Antwort
                    </Button>
                  )}
                </div>
              )}
              <Checkbox label="Pflichtfrage" checked={question.required} onChange={(value) => updateQuestion(question.key, { required: value })} />
            </div>
          ))}
          {questions.length < 20 && (
            <Button type="button" variant="secondary" onClick={() => setQuestions((list) => [...list, newQuestion('SINGLE')])}>
              + Frage
            </Button>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-4">
          <SectionTitle title="Einstellungen" />
          <Field label="Antworten möglich bis" hint="Leer lassen: läuft, bis du sie beendest.">
            {(id) => <Input id={id} type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />}
          </Field>
          <Checkbox label="Teilnehmende sehen nach ihrer Antwort das Gesamtergebnis" checked={showResults} onChange={setShowResults} />
          <div>
            <Checkbox label="Anonym" checked={anonymous} onChange={setAnonymous} />
            <p className="hint">
              Antworten werden ohne Namen gespeichert und lassen sich nicht mehr ändern. Du siehst nur, wer schon
              geantwortet hat – nicht was.
            </p>
          </div>
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" loading={create.isPending} disabled={problems.length > 0}>
          Umfrage starten
        </Button>
        {problems.length > 0 && <span className="text-sm text-slate-500">{problems.join(' · ')}</span>}
      </div>
    </form>
  );
}
